// WhatIfAnalyzer.js - Advanced What-If Portfolio Analysis Component
// Calculates how simulated trades would impact your real portfolio's risk metrics

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Plus,
  X,
  Search,
  Lightbulb,
  BarChart3,
  Shield,
  Target,
  AlertTriangle,
  Check,
  ArrowRight,
  Loader,
  PieChart,
  Activity,
  Zap,
  HelpCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

const API_BASE = 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Historical volatility data for common stocks (annualized %)
const STOCK_VOLATILITY = {
  'AAPL': 28, 'MSFT': 26, 'GOOGL': 30, 'AMZN': 35, 'META': 40, 'NVDA': 50,
  'TSLA': 60, 'AMD': 55, 'INTC': 35, 'CRM': 35, 'ORCL': 28, 'ADBE': 32,
  'JPM': 25, 'BAC': 30, 'WFC': 32, 'GS': 30, 'MS': 32, 'C': 35,
  'V': 22, 'MA': 24, 'PYPL': 45,
  'JNJ': 18, 'UNH': 22, 'PFE': 25, 'MRK': 22, 'ABBV': 25, 'LLY': 28,
  'XOM': 28, 'CVX': 28, 'COP': 35,
  'PG': 18, 'KO': 18, 'PEP': 18, 'WMT': 20, 'COST': 22,
  'HD': 25, 'MCD': 18, 'NKE': 30, 'SBUX': 28,
  'DIS': 32, 'NFLX': 45, 'T': 22, 'VZ': 20,
  'BA': 40, 'CAT': 28, 'GE': 35, 'UPS': 25,
  'DEFAULT': 30
};

// Beta values for common stocks
const STOCK_BETA = {
  'AAPL': 1.25, 'MSFT': 1.1, 'GOOGL': 1.15, 'AMZN': 1.3, 'META': 1.35, 'NVDA': 1.7,
  'TSLA': 2.0, 'AMD': 1.8, 'INTC': 1.1, 'CRM': 1.2, 'ORCL': 0.95, 'ADBE': 1.15,
  'JPM': 1.15, 'BAC': 1.4, 'WFC': 1.3, 'GS': 1.35, 'MS': 1.4, 'C': 1.5,
  'V': 1.0, 'MA': 1.05, 'PYPL': 1.5,
  'JNJ': 0.7, 'UNH': 0.85, 'PFE': 0.7, 'MRK': 0.65, 'ABBV': 0.75, 'LLY': 0.6,
  'XOM': 0.9, 'CVX': 1.0, 'COP': 1.2,
  'PG': 0.45, 'KO': 0.6, 'PEP': 0.6, 'WMT': 0.5, 'COST': 0.75,
  'HD': 1.05, 'MCD': 0.65, 'NKE': 1.1, 'SBUX': 1.0,
  'DIS': 1.25, 'NFLX': 1.4, 'T': 0.7, 'VZ': 0.45,
  'BA': 1.5, 'CAT': 1.1, 'GE': 1.2, 'UPS': 1.0,
  'DEFAULT': 1.0
};

// Sector mapping
const SECTOR_MAP = {
  'AAPL': 'Technology', 'MSFT': 'Technology', 'GOOGL': 'Technology', 'AMZN': 'Technology',
  'META': 'Technology', 'NVDA': 'Technology', 'TSLA': 'Consumer Discretionary', 'AMD': 'Technology',
  'INTC': 'Technology', 'CRM': 'Technology', 'ORCL': 'Technology', 'ADBE': 'Technology',
  'JPM': 'Financials', 'BAC': 'Financials', 'WFC': 'Financials', 'GS': 'Financials',
  'MS': 'Financials', 'C': 'Financials', 'V': 'Financials', 'MA': 'Financials',
  'JNJ': 'Healthcare', 'UNH': 'Healthcare', 'PFE': 'Healthcare', 'MRK': 'Healthcare',
  'ABBV': 'Healthcare', 'LLY': 'Healthcare', 'TMO': 'Healthcare', 'ABT': 'Healthcare',
  'XOM': 'Energy', 'CVX': 'Energy', 'COP': 'Energy', 'SLB': 'Energy',
  'PG': 'Consumer Staples', 'KO': 'Consumer Staples', 'PEP': 'Consumer Staples', 'WMT': 'Consumer Staples',
  'HD': 'Consumer Discretionary', 'MCD': 'Consumer Discretionary', 'NKE': 'Consumer Discretionary',
  'DIS': 'Communication Services', 'NFLX': 'Communication Services', 'T': 'Communication Services',
  'VZ': 'Communication Services', 'CMCSA': 'Communication Services',
  'BA': 'Industrials', 'CAT': 'Industrials', 'GE': 'Industrials', 'UPS': 'Industrials',
};

const SECTOR_COLORS = {
  'Technology': '#3b82f6',
  'Financials': '#22c55e',
  'Healthcare': '#ef4444',
  'Energy': '#f59e0b',
  'Consumer Staples': '#8b5cf6',
  'Consumer Discretionary': '#ec4899',
  'Communication Services': '#06b6d4',
  'Industrials': '#64748b',
  'Utilities': '#84cc16',
  'Real Estate': '#f97316',
  'Materials': '#a855f7',
  'Other': '#6b7280',
};

// Correlation matrix between sectors (simplified)
const SECTOR_CORRELATIONS = {
  'Technology-Technology': 1.0,
  'Technology-Financials': 0.65,
  'Technology-Healthcare': 0.45,
  'Technology-Energy': 0.35,
  'Technology-Consumer Staples': 0.30,
  'Technology-Consumer Discretionary': 0.70,
  'Financials-Financials': 1.0,
  'Financials-Healthcare': 0.50,
  'Financials-Energy': 0.55,
  'Financials-Consumer Staples': 0.40,
  'Healthcare-Healthcare': 1.0,
  'Healthcare-Energy': 0.30,
  'Healthcare-Consumer Staples': 0.55,
  'Energy-Energy': 1.0,
  'Energy-Consumer Staples': 0.25,
  'Consumer Staples-Consumer Staples': 1.0,
  'DEFAULT': 0.50
};

const getCorrelation = (sector1, sector2) => {
  if (sector1 === sector2) return 1.0;
  const key1 = `${sector1}-${sector2}`;
  const key2 = `${sector2}-${sector1}`;
  return SECTOR_CORRELATIONS[key1] || SECTOR_CORRELATIONS[key2] || SECTOR_CORRELATIONS['DEFAULT'];
};

const WhatIfAnalyzer = ({ realPortfolio, onExecuteInPaperTrading }) => {
  const [simulatedTrades, setSimulatedTrades] = useState([]);
  const [symbol, setSymbol] = useState('');
  const [amount, setAmount] = useState('');
  const [action, setAction] = useState('buy');
  const [searchResults, setSearchResults] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Debug: Log realPortfolio when component mounts or updates
  useEffect(() => {
    console.log('WhatIfAnalyzer received realPortfolio:', realPortfolio);
  }, [realPortfolio]);

  const userId = (() => {
    const storedUser = localStorage.getItem('investorq_user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser).id;
      } catch (e) {
        return null;
      }
    }
    return null;
  })();

  // Search stocks
  const handleSearch = async (query) => {
    setSymbol(query);
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/paper-trading/assets/search?q=${encodeURIComponent(query)}`, {
        headers: { 'x-user-id': String(userId) },
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.assets || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const selectStock = (asset) => {
    setSymbol(asset.symbol);
    setSearchResults([]);
  };

  // Add simulated trade
  const addTrade = async () => {
    if (!symbol || !amount) return;

    setAnalyzing(true);
    try {
      // Fetch current price
      const quoteRes = await fetch(`${API_BASE}/paper-trading/quote/${symbol}`, {
        headers: { 'x-user-id': String(userId) },
      });

      let price = 100;
      if (quoteRes.ok) {
        const quoteData = await quoteRes.json();
        price = quoteData.price;
      }

      const amountNum = parseFloat(amount);
      const shares = amountNum / price;
      const sector = SECTOR_MAP[symbol.toUpperCase()] || 'Other';
      const volatility = STOCK_VOLATILITY[symbol.toUpperCase()] || STOCK_VOLATILITY['DEFAULT'];
      const beta = STOCK_BETA[symbol.toUpperCase()] || STOCK_BETA['DEFAULT'];

      const newTrade = {
        id: Date.now(),
        symbol: symbol.toUpperCase(),
        action,
        amount: amountNum,
        shares,
        price,
        sector,
        volatility,
        beta,
      };

      const updatedTrades = [...simulatedTrades, newTrade];
      setSimulatedTrades(updatedTrades);
      setSymbol('');
      setAmount('');

      // Run analysis
      runAdvancedAnalysis(updatedTrades);
    } catch (err) {
      console.error('Error adding trade:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const removeTrade = (id) => {
    const updated = simulatedTrades.filter(t => t.id !== id);
    setSimulatedTrades(updated);
    if (updated.length > 0) {
      runAdvancedAnalysis(updated);
    } else {
      setAnalysis(null);
    }
  };

  // Advanced portfolio analysis
  const runAdvancedAnalysis = (trades) => {
    // Get current portfolio holdings
    const currentHoldings = realPortfolio?.holdings || [];
    const currentTotalValue = realPortfolio?.totalValue || 0;

    console.log('WhatIf Analysis - realPortfolio:', realPortfolio);
    console.log('WhatIf Analysis - holdings:', currentHoldings);
    console.log('WhatIf Analysis - totalValue:', currentTotalValue);

    // Build current portfolio with risk data
    const currentPortfolio = currentHoldings.map(h => ({
      symbol: h.symbol,
      value: h.currentValue || h.value || h.totalValue || h.marketValue || (h.shares * (h.currentPrice || h.current_price || 0)) || 0,
      sector: SECTOR_MAP[h.symbol] || 'Other',
      volatility: STOCK_VOLATILITY[h.symbol] || STOCK_VOLATILITY['DEFAULT'],
      beta: STOCK_BETA[h.symbol] || STOCK_BETA['DEFAULT'],
    }));

    // Build simulated portfolio (current + trades)
    const simulatedPortfolio = [...currentPortfolio];
    let simulatedTotalValue = currentTotalValue;

    trades.forEach(trade => {
      if (trade.action === 'buy') {
        simulatedTotalValue += trade.amount;
        
        // Check if already in portfolio
        const existing = simulatedPortfolio.find(p => p.symbol === trade.symbol);
        if (existing) {
          existing.value += trade.amount;
        } else {
          simulatedPortfolio.push({
            symbol: trade.symbol,
            value: trade.amount,
            sector: trade.sector,
            volatility: trade.volatility,
            beta: trade.beta,
          });
        }
      } else {
        simulatedTotalValue -= trade.amount;
        const existing = simulatedPortfolio.find(p => p.symbol === trade.symbol);
        if (existing) {
          existing.value = Math.max(0, existing.value - trade.amount);
        }
      }
    });

    // Filter out zero-value positions
    const filteredSimulated = simulatedPortfolio.filter(p => p.value > 0);

    // Calculate metrics for both portfolios
    const currentMetrics = calculatePortfolioMetrics(currentPortfolio, currentTotalValue);
    const simulatedMetrics = calculatePortfolioMetrics(filteredSimulated, simulatedTotalValue);

    // Generate comparison and recommendations
    const comparison = generateComparison(currentMetrics, simulatedMetrics, trades);

    setAnalysis({
      current: currentMetrics,
      simulated: simulatedMetrics,
      comparison,
      trades,
    });
  };

  const calculatePortfolioMetrics = (holdings, totalValue) => {
    if (!holdings.length || totalValue <= 0) {
      return {
        totalValue: 0,
        holdings: 0,
        volatility: 0,
        beta: 0,
        sharpeRatio: 0,
        var95: 0,
        maxDrawdown: 0,
        diversificationScore: 0,
        hhi: 10000,
        sectorExposure: {},
        topConcentration: 100,
      };
    }

    // Calculate weights
    const weights = holdings.map(h => h.value / totalValue);

    // Calculate weighted beta
    const weightedBeta = holdings.reduce((sum, h, i) => sum + (h.beta * weights[i]), 0);

    // Calculate portfolio volatility (simplified - assumes some correlation)
    let portfolioVariance = 0;
    for (let i = 0; i < holdings.length; i++) {
      for (let j = 0; j < holdings.length; j++) {
        const correlation = getCorrelation(holdings[i].sector, holdings[j].sector);
        portfolioVariance += weights[i] * weights[j] * holdings[i].volatility * holdings[j].volatility * correlation / 100;
      }
    }
    const portfolioVolatility = Math.sqrt(portfolioVariance);

    // Sharpe Ratio (assuming 5% risk-free rate, 10% expected return)
    const riskFreeRate = 5;
    const expectedReturn = 8 + (weightedBeta - 1) * 5; // Simple CAPM-like estimate
    const sharpeRatio = portfolioVolatility > 0 ? (expectedReturn - riskFreeRate) / portfolioVolatility : 0;

    // Value at Risk (95% confidence, 1 day)
    const var95 = portfolioVolatility * 1.645 / Math.sqrt(252); // Daily VaR

    // Max Drawdown estimate (based on volatility)
    const maxDrawdown = portfolioVolatility * 2.5; // Rough estimate

    // Sector exposure
    const sectorExposure = {};
    holdings.forEach((h, i) => {
      sectorExposure[h.sector] = (sectorExposure[h.sector] || 0) + weights[i] * 100;
    });

    // HHI (Herfindahl-Hirschman Index)
    const hhi = weights.reduce((sum, w) => sum + Math.pow(w * 100, 2), 0);

    // Diversification score (0-100, higher is better)
    const diversificationScore = Math.min(100, Math.max(0, 
      100 - (hhi / 100) + (Object.keys(sectorExposure).length * 10)
    ));

    // Top concentration
    const topConcentration = Math.max(...Object.values(sectorExposure));

    return {
      totalValue,
      holdings: holdings.length,
      volatility: portfolioVolatility,
      beta: weightedBeta,
      sharpeRatio,
      var95,
      maxDrawdown,
      diversificationScore,
      hhi,
      sectorExposure,
      topConcentration,
      expectedReturn,
    };
  };

  const generateComparison = (current, simulated, trades) => {
    const improvements = [];
    const concerns = [];
    let overallScore = 0;

    // Volatility change
    const volChange = simulated.volatility - current.volatility;
    if (volChange < -1) {
      improvements.push(`Reduces volatility by ${Math.abs(volChange).toFixed(1)}%`);
      overallScore += 2;
    } else if (volChange > 2) {
      concerns.push(`Increases volatility by ${volChange.toFixed(1)}%`);
      overallScore -= 2;
    }

    // Beta change
    const betaChange = simulated.beta - current.beta;
    if (Math.abs(betaChange) > 0.05) {
      if (betaChange < 0) {
        improvements.push(`Lowers market sensitivity (Beta: ${current.beta.toFixed(2)} → ${simulated.beta.toFixed(2)})`);
        overallScore += 1;
      } else {
        concerns.push(`Increases market sensitivity (Beta: ${current.beta.toFixed(2)} → ${simulated.beta.toFixed(2)})`);
        overallScore -= 1;
      }
    }

    // Sharpe Ratio change
    const sharpeChange = simulated.sharpeRatio - current.sharpeRatio;
    if (sharpeChange > 0.05) {
      improvements.push(`Improves risk-adjusted returns (Sharpe: ${current.sharpeRatio.toFixed(2)} → ${simulated.sharpeRatio.toFixed(2)})`);
      overallScore += 2;
    } else if (sharpeChange < -0.1) {
      concerns.push(`Reduces risk-adjusted returns (Sharpe: ${current.sharpeRatio.toFixed(2)} → ${simulated.sharpeRatio.toFixed(2)})`);
      overallScore -= 2;
    }

    // Diversification
    const divChange = simulated.diversificationScore - current.diversificationScore;
    if (divChange > 5) {
      improvements.push(`Improves diversification score (${current.diversificationScore.toFixed(0)} → ${simulated.diversificationScore.toFixed(0)})`);
      overallScore += 2;
    } else if (divChange < -5) {
      concerns.push(`Reduces diversification (${current.diversificationScore.toFixed(0)} → ${simulated.diversificationScore.toFixed(0)})`);
      overallScore -= 1;
    }

    // Concentration
    const concChange = simulated.topConcentration - current.topConcentration;
    if (concChange < -5) {
      improvements.push(`Reduces sector concentration (${current.topConcentration.toFixed(0)}% → ${simulated.topConcentration.toFixed(0)}%)`);
      overallScore += 1;
    } else if (concChange > 10) {
      concerns.push(`Increases sector concentration (${current.topConcentration.toFixed(0)}% → ${simulated.topConcentration.toFixed(0)}%)`);
      overallScore -= 1;
    }

    // New sectors
    const newSectors = Object.keys(simulated.sectorExposure).filter(
      s => !current.sectorExposure[s] || current.sectorExposure[s] < 1
    );
    if (newSectors.length > 0) {
      improvements.push(`Adds exposure to ${newSectors.join(', ')}`);
      overallScore += newSectors.length;
    }

    // VaR change
    const varChange = simulated.var95 - current.var95;
    if (varChange < -0.1) {
      improvements.push(`Reduces daily Value-at-Risk`);
      overallScore += 1;
    } else if (varChange > 0.2) {
      concerns.push(`Increases daily Value-at-Risk`);
      overallScore -= 1;
    }

    // Generate verdict
    let verdict, verdictType;
    if (overallScore >= 3) {
      verdict = 'Highly Beneficial - This trade would significantly improve your portfolio metrics.';
      verdictType = 'positive';
    } else if (overallScore >= 1) {
      verdict = 'Moderately Beneficial - This trade shows potential improvements with minimal risks.';
      verdictType = 'positive';
    } else if (overallScore >= -1) {
      verdict = 'Neutral Impact - This trade has mixed effects on your portfolio.';
      verdictType = 'neutral';
    } else {
      verdict = 'Consider Carefully - This trade may increase portfolio risk.';
      verdictType = 'negative';
    }

    return {
      improvements,
      concerns,
      overallScore,
      verdict,
      verdictType,
    };
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return 'N/A';
    const sign = value >= 0 ? '' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getChangeColor = (current, simulated, lowerIsBetter = false) => {
    const diff = simulated - current;
    if (Math.abs(diff) < 0.01) return '#94a3b8';
    if (lowerIsBetter) {
      return diff < 0 ? '#22c55e' : '#ef4444';
    }
    return diff > 0 ? '#22c55e' : '#ef4444';
  };

  const getChangeArrow = (current, simulated, lowerIsBetter = false) => {
    const diff = simulated - current;
    if (Math.abs(diff) < 0.01) return '';
    if (lowerIsBetter) {
      return diff < 0 ? ' ↓' : ' ↑';
    }
    return diff > 0 ? ' ↑' : ' ↓';
  };

  // Prepare radar chart data
  const getRadarData = () => {
    if (!analysis) return [];
    return [
      { metric: 'Diversification', current: analysis.current.diversificationScore, simulated: analysis.simulated.diversificationScore, fullMark: 100 },
      { metric: 'Stability', current: Math.max(0, 100 - analysis.current.volatility * 2), simulated: Math.max(0, 100 - analysis.simulated.volatility * 2), fullMark: 100 },
      { metric: 'Sharpe', current: Math.min(100, analysis.current.sharpeRatio * 50), simulated: Math.min(100, analysis.simulated.sharpeRatio * 50), fullMark: 100 },
      { metric: 'Low Risk', current: Math.max(0, 100 - analysis.current.var95 * 20), simulated: Math.max(0, 100 - analysis.simulated.var95 * 20), fullMark: 100 },
      { metric: 'Balance', current: Math.max(0, 100 - analysis.current.topConcentration), simulated: Math.max(0, 100 - analysis.simulated.topConcentration), fullMark: 100 },
    ];
  };

  // Prepare sector comparison data
  const getSectorData = () => {
    if (!analysis) return [];
    const sectors = [...new Set([
      ...Object.keys(analysis.current.sectorExposure),
      ...Object.keys(analysis.simulated.sectorExposure)
    ])];
    return sectors.map(sector => ({
      sector,
      current: analysis.current.sectorExposure[sector] || 0,
      simulated: analysis.simulated.sectorExposure[sector] || 0,
    })).sort((a, b) => b.simulated - a.simulated);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '1rem',
      padding: '1.5rem',
      marginBottom: '1.5rem'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <Lightbulb size={24} color="#3b82f6" />
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>What-If Portfolio Analyzer</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 0 }}>
            See how trades would impact your actual portfolio's risk metrics
          </p>
        </div>
      </div>

      {/* Add Trade Form */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1rem', 
        flexWrap: 'wrap',
        alignItems: 'flex-end'
      }}>
        <div style={{ flex: '1', minWidth: '150px', position: 'relative' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Symbol</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} color="#64748b" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={symbol}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="AAPL, BAC..."
              style={{
                width: '100%',
                padding: '0.625rem 0.625rem 0.625rem 2rem',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.875rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          {searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              marginTop: '0.25rem',
              maxHeight: '150px',
              overflowY: 'auto',
              zIndex: 10
            }}>
              {searchResults.slice(0, 5).map((asset) => (
                <button
                  key={asset.symbol}
                  onClick={() => selectStock(asset)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #334155',
                    color: 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.8rem'
                  }}
                >
                  <strong>{asset.symbol}</strong> <span style={{ color: '#64748b' }}>{asset.name?.substring(0, 25)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: '100px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Action</label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem',
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.875rem'
            }}
          >
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
        </div>

        <div style={{ flex: '1', minWidth: '120px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Amount ($)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10000"
            style={{
              width: '100%',
              padding: '0.625rem',
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.875rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <button
          onClick={addTrade}
          disabled={!symbol || !amount || analyzing}
          style={{
            padding: '0.625rem 1.25rem',
            background: '#22c55e',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            opacity: (!symbol || !amount || analyzing) ? 0.5 : 1
          }}
        >
          {analyzing ? <Loader size={16} className="spin" /> : <Plus size={16} />}
          Add
        </button>
      </div>

      {/* Simulated Trades List */}
      {simulatedTrades.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Simulated Trades:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {simulatedTrades.map(trade => (
              <div key={trade.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: trade.action === 'buy' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: `1px solid ${trade.action === 'buy' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                borderRadius: '0.5rem'
              }}>
                <span style={{ color: trade.action === 'buy' ? '#34d399' : '#f87171', fontWeight: '600', fontSize: '0.75rem' }}>
                  {trade.action.toUpperCase()}
                </span>
                <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{trade.symbol}</span>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>${trade.amount.toLocaleString()}</span>
                <span style={{ color: '#64748b', fontSize: '0.7rem' }}>({trade.sector})</span>
                <button
                  onClick={() => removeTrade(trade.id)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0.125rem' }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1.25rem' }}>
          
          {/* Risk Metrics Comparison */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Shield size={18} color="#3b82f6" />
              Advanced Risk Metrics Comparison
            </h3>
            <button
              onClick={() => setShowHelp(!showHelp)}
              style={{
                padding: '0.375rem 0.75rem',
                background: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '0.375rem',
                color: '#93c5fd',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}
            >
              <HelpCircle size={14} />
              {showHelp ? 'Hide' : 'How to Read'}
            </button>
          </div>

          {/* Educational Help Section */}
          {showHelp && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1rem',
              fontSize: '0.8rem',
              lineHeight: '1.5'
            }}>
              <h4 style={{ color: '#93c5fd', marginTop: 0, marginBottom: '0.75rem', fontSize: '0.875rem' }}>📊 Understanding the Metrics</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Volatility</strong>
                  <p style={{ color: '#94a3b8', margin: '0.25rem 0' }}>How much your portfolio value swings. <span style={{ color: '#34d399' }}>Lower is safer</span>. 20% means your portfolio could move ±20% in a year.</p>
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Beta</strong>
                  <p style={{ color: '#94a3b8', margin: '0.25rem 0' }}>Sensitivity to market moves. Beta=1 moves with market. <span style={{ color: '#34d399' }}>&lt;1 is defensive</span>, &gt;1 is aggressive.</p>
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Sharpe Ratio</strong>
                  <p style={{ color: '#94a3b8', margin: '0.25rem 0' }}>Return per unit of risk. <span style={{ color: '#34d399' }}>Higher is better</span>. &gt;1 is good, &gt;2 is excellent.</p>
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>VaR (95%)</strong>
                  <p style={{ color: '#94a3b8', margin: '0.25rem 0' }}>Max daily loss 95% of the time. 2% VaR on $100K = potential $2K daily loss. <span style={{ color: '#34d399' }}>Lower is safer</span>.</p>
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Diversification</strong>
                  <p style={{ color: '#94a3b8', margin: '0.25rem 0' }}>Score 0-100 measuring spread across sectors. <span style={{ color: '#34d399' }}>Higher means less concentrated risk</span>.</p>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.375rem', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                <strong style={{ color: '#34d399' }}>Reading the Colors:</strong>
                <span style={{ color: '#94a3b8' }}> Green ↓ on Volatility/Beta/VaR = improvement. Green ↑ on Sharpe/Diversification = improvement.</span>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {/* Portfolio Value */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Activity size={12} /> Portfolio Value
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(analysis.current.totalValue)}</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#3b82f6' }}>{formatCurrency(analysis.simulated.totalValue)}</div>
                </div>
              </div>
            </div>

            {/* Volatility */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }} title="Lower volatility = less price swings = safer">
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Zap size={12} /> Volatility <span style={{ color: '#64748b', fontSize: '0.6rem' }}>(lower=safer)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{formatPercent(analysis.current.volatility)}</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: getChangeColor(analysis.current.volatility, analysis.simulated.volatility, true)
                  }}>
                    {formatPercent(analysis.simulated.volatility)}
                    {getChangeArrow(analysis.current.volatility, analysis.simulated.volatility, true)}
                  </div>
                </div>
              </div>
            </div>

            {/* Beta */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }} title="Beta < 1 = defensive, Beta > 1 = aggressive">
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <BarChart3 size={12} /> Beta <span style={{ color: '#64748b', fontSize: '0.6rem' }}>(1=market)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{analysis.current.beta.toFixed(2)}</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: getChangeColor(analysis.current.beta, analysis.simulated.beta, true)
                  }}>
                    {analysis.simulated.beta.toFixed(2)}
                    {getChangeArrow(analysis.current.beta, analysis.simulated.beta, true)}
                  </div>
                </div>
              </div>
            </div>

            {/* Sharpe Ratio */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }} title="Higher Sharpe = better risk-adjusted returns">
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Target size={12} /> Sharpe Ratio <span style={{ color: '#64748b', fontSize: '0.6rem' }}>(higher=better)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{analysis.current.sharpeRatio.toFixed(2)}</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: getChangeColor(analysis.current.sharpeRatio, analysis.simulated.sharpeRatio)
                  }}>
                    {analysis.simulated.sharpeRatio.toFixed(2)}
                    {getChangeArrow(analysis.current.sharpeRatio, analysis.simulated.sharpeRatio)}
                  </div>
                </div>
              </div>
            </div>

            {/* VaR */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }} title="Max expected daily loss with 95% confidence">
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <AlertTriangle size={12} /> VaR (95%) <span style={{ color: '#64748b', fontSize: '0.6rem' }}>(lower=safer)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{formatPercent(analysis.current.var95)}</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: getChangeColor(analysis.current.var95, analysis.simulated.var95, true)
                  }}>
                    {formatPercent(analysis.simulated.var95)}
                    {getChangeArrow(analysis.current.var95, analysis.simulated.var95, true)}
                  </div>
                </div>
              </div>
            </div>

            {/* Diversification Score */}
            <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: '0.5rem', padding: '0.875rem' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <PieChart size={12} /> Diversification
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Current</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>{analysis.current.diversificationScore.toFixed(0)}/100</div>
                </div>
                <ArrowRight size={14} color="#64748b" />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Simulated</div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: getChangeColor(analysis.current.diversificationScore, analysis.simulated.diversificationScore)
                  }}>
                    {analysis.simulated.diversificationScore.toFixed(0)}/100
                    {getChangeArrow(analysis.current.diversificationScore, analysis.simulated.diversificationScore)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Radar Chart */}
            <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.5rem', padding: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#94a3b8' }}>Risk Profile Comparison</h4>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={getRadarData()}>
                  <PolarGrid stroke="#374151" />
                  <PolarAngleAxis dataKey="metric" stroke="#9ca3af" fontSize={10} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#374151" fontSize={8} />
                  <Radar name="Current" dataKey="current" stroke="#64748b" fill="#64748b" fillOpacity={0.3} />
                  <Radar name="Simulated" dataKey="simulated" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Sector Allocation Chart */}
            <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.5rem', padding: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#94a3b8' }}>Sector Allocation</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getSectorData()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" tickFormatter={(v) => `${v.toFixed(0)}%`} fontSize={10} />
                  <YAxis dataKey="sector" type="category" stroke="#9ca3af" width={80} fontSize={9} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '0.5rem', fontSize: '11px' }}
                    formatter={(value) => [`${value.toFixed(1)}%`]}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="current" name="Current" fill="#64748b" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="simulated" name="Simulated" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AI Recommendation */}
          <div style={{
            background: analysis.comparison.verdictType === 'positive' ? 'rgba(34, 197, 94, 0.1)' : 
                       analysis.comparison.verdictType === 'negative' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(251, 191, 36, 0.1)',
            border: `1px solid ${analysis.comparison.verdictType === 'positive' ? 'rgba(34, 197, 94, 0.3)' : 
                                 analysis.comparison.verdictType === 'negative' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
            borderRadius: '0.75rem',
            padding: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {analysis.comparison.verdictType === 'positive' ? (
                <Check size={20} color="#22c55e" />
              ) : analysis.comparison.verdictType === 'negative' ? (
                <AlertTriangle size={20} color="#ef4444" />
              ) : (
                <AlertTriangle size={20} color="#fbbf24" />
              )}
              <span style={{ 
                fontWeight: '600', 
                color: analysis.comparison.verdictType === 'positive' ? '#34d399' : 
                       analysis.comparison.verdictType === 'negative' ? '#f87171' : '#fbbf24'
              }}>
                AI Portfolio Analysis
              </span>
            </div>

            {analysis.comparison.improvements.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '600', marginBottom: '0.25rem' }}>Improvements:</div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#e2e8f0', fontSize: '0.8rem' }}>
                  {analysis.comparison.improvements.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.125rem' }}>✅ {item}</li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.comparison.concerns.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#f87171', fontWeight: '600', marginBottom: '0.25rem' }}>Concerns:</div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#e2e8f0', fontSize: '0.8rem' }}>
                  {analysis.comparison.concerns.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.125rem' }}>⚠️ {item}</li>
                  ))}
                </ul>
              </div>
            )}

            <p style={{ 
              margin: 0, 
              padding: '0.75rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '0.5rem',
              color: '#e2e8f0', 
              fontSize: '0.875rem', 
              fontWeight: '500'
            }}>
              📊 {analysis.comparison.verdict}
            </p>
          </div>

          {/* Execute Button */}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              onClick={() => {
                setSimulatedTrades([]);
                setAnalysis(null);
              }}
              style={{
                padding: '0.75rem 1.25rem',
                background: '#475569',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Clear All
            </button>
            <button
              onClick={() => onExecuteInPaperTrading && onExecuteInPaperTrading(simulatedTrades)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              <Target size={16} />
              Execute in Paper Trading
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default WhatIfAnalyzer;